import { useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 180000;

/**
 * Custom hook: submits an assessment then polls the status endpoint until ready.
 * Handles React 18 StrictMode double-invoke via refs (survive remount).
 * Calls onDone(finalDoc) when status flips to 'ready', or onError(err) on failure.
 */
export default function useAnalysisPolling({ answers, lead, onDone, onError }) {
  const firedRef = useRef(false);
  const cancelledRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    cancelledRef.current = false;
    if (firedRef.current) return; // StrictMode remount — mount#1's async chain is still alive.
    firedRef.current = true;

    submitAndPoll({ answers, lead, cancelledRef, onDone, onError });

    return () => { /* intentionally no cancellation — see PollHook notes */ };
  }, []);
}

async function submitAndPoll({ answers, lead, cancelledRef, onDone, onError }) {
  const startedAt = Date.now();
  try {
    const submitRes = await axios.post(`${API}/assessment/submit`, {
      answers,
      name: lead.name,
      email: lead.email,
      business_name: lead.business_name,
      website_url: lead.website_url || null,
    });
    const id = submitRes.data?.id;
    if (!id) throw new Error("Unexpected server response.");
    const finalDoc = await pollUntilReady(id, startedAt, cancelledRef);
    if (cancelledRef.current || !finalDoc) return;
    onDone(shapeResult(finalDoc));
  } catch (e) {
    if (cancelledRef.current) return;
    const msg = e?.response?.data?.detail || e?.message || "Something went wrong. Please try again.";
    onError(msg);
  }
}

async function pollUntilReady(id, startedAt, cancelledRef) {
  while (!cancelledRef.current) {
    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
      throw new Error("Analysis is taking longer than expected. Please try again in a moment.");
    }
    const doc = await tryFetchStatus(id);
    if (doc && doc.status === "ready") return doc;
    if (doc && doc.status === "failed") {
      throw new Error(doc.error || "Analysis failed. Please try again.");
    }
    await sleep(POLL_INTERVAL_MS);
  }
  return null;
}

async function tryFetchStatus(id) {
  try {
    const s = await axios.get(`${API}/assessment/${id}/status`);
    return s.data;
  } catch (err) {
    // Swallow transient/network errors and keep polling; surface only real 4xx/5xx (not 404s during initial insert race).
    const code = err?.response?.status;
    if (code && code !== 404 && code >= 400) throw err;
    return null;
  }
}

function shapeResult(finalDoc) {
  return {
    id: finalDoc.id,
    diagnosis: finalDoc.diagnosis,
    critique: finalDoc.critique,
    prescription: finalDoc.prescription,
    gaps: finalDoc.gaps,
    site_data: finalDoc.site_data || {},
    email_status: finalDoc.email_status || {},
  };
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
